// src/pages/ResetPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Login.css'; // reuse existing styles

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token') || '';
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);

    // If no token in URL, show an error immediately
    useEffect(() => {
        if (!token) {
            setError('Manjkajoči token. Uporabite povezavo iz vašega e-poštnega sporočila.');
        }
    }, [token]);

    // --- Password Strength Calculation (same as in LoginRegisterPage) ---
    const calculatePasswordStrength = (pwd) => {
        let score = 0;
        if (!pwd) return { score: 0, text: '', color: '' };

        if (pwd.length >= 8) score++;
        if (pwd.length >= 12) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/\d/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;

        let strengthLevel = Math.min(Math.floor(score / 1.5), 3);

        switch (strengthLevel) {
            case 0:
                return { score: 1, text: 'Šibko', color: '#FF4136' };
            case 1:
                return { score: 2, text: 'Srednje', color: '#FF851B' };
            case 2:
                return { score: 3, text: 'Močno', color: '#2ECC40' };
            case 3:
                return { score: 4, text: 'Zelo močno', color: '#0074D9' };
            default:
                return { score: 0, text: '', color: '' };
        }
    };

    const passwordStrength = calculatePasswordStrength(newPassword);

    // --- Password Match Check ---
    const passwordsMatch = newPassword === confirmPassword;
    const showMatchError = confirmPassword && !passwordsMatch;
    const showMatchSuccess = confirmPassword && passwordsMatch;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!token) return;

        if (newPassword.length < 8) {
            setError('Geslo mora biti vsaj 8 znakov dolgo.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Gesli se ne ujemata.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword }),
                credentials: 'include',
            });
            const data = await response.json();
            if (response.ok) {
                setSuccess('Geslo je bilo uspešno ponastavljeno. Preusmerjam na prijavo...');
                setTimeout(() => navigate('/prijava'), 2000);
            } else {
                setError(data.message || 'Napaka pri ponastavitvi gesla.');
            }
        } catch {
            setError('Ni mogoče vzpostaviti povezave s strežnikom. Poskusite pozneje.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg">
            <section className="sign_in_area pt-120 pb-120">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-5 col-md-7 col-sm-9">
                            <div className="sign_in_form">
                                <div className="sign_title">
                                    <h5 className="title">Ponastavitev gesla</h5>
                                </div>

                                {error && <div className="alert alert-danger">{error}</div>}
                                {success && <div className="alert alert-success">{success}</div>}

                                {!success && (
                                    <form onSubmit={handleSubmit}>
                                        <div className="sign_form_wrapper">
                                            {/* --- New Password Field --- */}
                                            <div className="single_form password-input-container">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder="Novo geslo"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    required
                                                />
                                                <i className="fa fa-key input-icon-left"></i>
                                                <i
                                                    className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon-right`}
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    aria-label={showPassword ? 'Skrij geslo' : 'Pokaži geslo'}
                                                ></i>
                                            </div>

                                            {/* --- Password Strength Bar --- */}
                                            {newPassword && (
                                                <div className="password-strength-container">
                                                    <div className="password-strength-bar">
                                                        <div
                                                            className="password-strength-fill"
                                                            style={{
                                                                width: `${(passwordStrength.score / 4) * 100}%`,
                                                                backgroundColor: passwordStrength.color,
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <span className="password-strength-text" style={{ color: passwordStrength.color }}>
                                                        {passwordStrength.text}
                                                    </span>
                                                </div>
                                            )}

                                            {/* --- Confirm Password Field --- */}
                                            <div className="single_form password-input-container">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    placeholder="Potrdi novo geslo"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                />
                                                <i className="fa fa-key input-icon-left"></i>
                                                <i
                                                    className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon-right`}
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    aria-label={showConfirmPassword ? 'Skrij potrditveno geslo' : 'Pokaži potrditveno geslo'}
                                                ></i>
                                            </div>

                                            {/* --- Password Match Indicator --- */}
                                            {confirmPassword && (
                                                <div
                                                    className={`password-match-indicator ${showMatchError ? 'error' : showMatchSuccess ? 'success' : ''
                                                        }`}
                                                >
                                                    {showMatchError && 'Gesli se ne ujemata'}
                                                    {showMatchSuccess && 'Gesli se ujemata'}
                                                </div>
                                            )}

                                            <div className="single_form">
                                                <button type="submit" className="btn btn-success post-item-navbar-btn" disabled={loading}>
                                                    {loading ? 'Ponastavljam...' : 'Ponastavi geslo'}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                )}

                                <div className="text-center mt-3">
                                    <small>
                                        Spomnite se gesla?{' '}
                                        <button
                                            className="btn btn-link p-0"
                                            type="button"
                                            onClick={() => navigate('/prijava')}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            Prijava
                                        </button>
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ResetPasswordPage;